import {Component, computed, effect, inject, OnDestroy, signal, Signal} from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {
  ISettingsState,
  IUserAccessFeature,
  TFeatures,
} from '../../../user-access/models/user-access-features/user-access-features';
import {
  selectUserAccess,
  selectUserAccessFeatureSettings,
} from '../../../user-access/user-access-store/user-access.selectors';
import {IUserAccess} from '../../../user-access/models/user-access';
import {selectDocumentLayoutContentLoading} from '../../store/document-layout.selectors';
import {
  selectSelectedNavigationSetting,
  selectSelectedSection,
  selectSelectedSettingComponent,
  selectSelectedSubSection,
  selectSettingState,
} from '../../store/setting.selectors';
import {ISettingsSection, SettingsReferenceEnum} from '../../models';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {settingsSetSelected} from '../../store/setting.actions';
import {NavigationPageEnum, NavigationPageType} from '../../../user-access/models/navigations.enums';
import {UnsavedChangesService} from '../../services';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {isStoreAdminForStore} from '../../../../shared/shared-utils/user-access/user-access.utils';
import {GALLIX_MICROSOFT_MAIL_MESSAGE} from '../../models/email/microsoft-mail.utils';
import {DEFAULT_LOADER_INFINITE} from '../../../../shared/shared-module-ui/models/animations';

@Component({
  selector: 'app-settings-layout',
  templateUrl: './settings-layout.component.html',
  styleUrl: './settings-layout.component.scss',
  standalone: false,
})
export class SettingsLayoutComponent implements OnDestroy {

  private _store = inject(Store);
  private readonly router = inject(Router);
  private readonly unsavedChangesService = inject(UnsavedChangesService);
  private collapseTimeout: ReturnType<typeof setTimeout> | null = null;

  protected readonly selectedSection: Signal<string> = this._store.selectSignal(selectSelectedSection);
  protected readonly selectedSubSection: Signal<string> = this._store.selectSignal(selectSelectedSubSection);
  protected readonly selectedSetting: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedNavigationSetting);
  protected readonly selectedSettingComponent = this._store.selectSignal(selectSelectedSettingComponent);
  protected readonly selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  protected readonly uafSettings: Signal<ISettingsState> = this._store.selectSignal(selectUserAccessFeatureSettings);
  private readonly _userAccess: Signal<IUserAccess> = this._store.selectSignal(selectUserAccess);
  /**
   * Per selected store — matches GallixRules `userIsStoreAdmin`.
   * Having admin on any other store must not unlock store settings here.
   */
  private readonly isAdmin = computed((): boolean =>
    isStoreAdminForStore(this._userAccess(), this.selectedStore()?.storeId),
  );

  /**
   * Collapsed Feature sidebar (icon rail) — auto-enabled on Label Maker for canvas width.
   */
  protected readonly isSidebarCollapsed = signal(false);

  protected readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly animation = DEFAULT_LOADER_INFINITE;
  private readonly settingState = this._store.selectSignal(selectSettingState);
  private readonly documentLayoutContentLoading = this._store.selectSignal(selectDocumentLayoutContentLoading);

  /**
   * Feature pages load inside the settings body. Show the same indicator used by Email Settings.
   */
  protected readonly isFeatureContentLoading = computed((): boolean => {
    if (this.selectedSection() !== 'Feature') {
      return false;
    }
    const loading = this.settingState().isLoading;
    switch (this.selectedSetting()) {
      case SettingsReferenceEnum.featureAutoOrderGeneralSettings:
        return loading.orderSettings || loading.openOrderSettings;
      case SettingsReferenceEnum.featureAutoOrderOrderSettings:
        return loading.orderSettings;
      case SettingsReferenceEnum.featureAutoOrderSchedulerSettings:
      case SettingsReferenceEnum.featureAutoOrderOpenOrderSettings:
        return loading.openOrderSettings;
      case SettingsReferenceEnum.featureAutoOrderDocumentLayouts:
        return this.documentLayoutContentLoading();
      default:
        return false;
    }
  });

  protected readonly sections = computed((): ISettingsSection[] => {
    const settings: ISettingsState = this.uafSettings();
    const isAdmin: boolean = this.isAdmin();

    const featureAufFeatures: TFeatures[] = isAdmin
      ? Object.values(settings.features || {})
      : this.filterNonAdminFeatures(Object.values(settings.features || {}));

    return [{
      icon: IconsEnum.accountCog,
      header: 'Account',
      aufFeatures: Object.values(settings.account || {}),
    }, {
      icon: IconsEnum.applicationCogOutline,
      header: 'User',
      aufFeatures: Object.values(settings.application || {}),
    }, {
      icon: IconsEnum.emailVariant,
      header: 'Email',
      aufFeatures: Object.values(settings.notifications || {}),
    }, {
      icon: IconsEnum.fileCogOutline,
      header: 'Feature',
      aufFeatures: featureAufFeatures,
    }];
  });

  protected readonly selectedSectionFeatures = computed((): TFeatures[] => {
    const currentSection: string = this.selectedSection();
    const allSections: ISettingsSection[] = this.sections();
    const section = allSections.find((s: ISettingsSection) => s.header === currentSection);
    return section?.aufFeatures || [];
  });

  protected readonly showSidebar = computed((): boolean => this.selectedSectionFeatures().length > 1);

  protected readonly isFeatureSection = computed((): boolean => this.selectedSection() === 'Feature');

  protected readonly isFeatureSidebarCollapsed = computed(
    (): boolean => this.isFeatureSection() && this.isSidebarCollapsed(),
  );

  protected readonly isLabelMakerSelected = computed(
    (): boolean => this.selectedSetting() === SettingsReferenceEnum.featureLabelMaker,
  );

  /**
   * Store-scoped Feature settings that write under `stores/{storeId}/settings/**`
   * and therefore require admin for the selected store.
   */
  private readonly storeAdminRequiredSettings: ReadonlySet<SettingsReferenceEnum> = new Set([
    SettingsReferenceEnum.featureAutoOrderOpenOrderSettings,
    SettingsReferenceEnum.featureAutoOrderSchedulerSettings,
    SettingsReferenceEnum.featureAutoOrderDocumentLayouts,
    SettingsReferenceEnum.featureShelfTalkerGeneral,
    SettingsReferenceEnum.featureLabelMaker,
    SettingsReferenceEnum.featureStockRulesStockItemRuleAdd,
    SettingsReferenceEnum.featureStockRulesStockItemRuleEnable,
    SettingsReferenceEnum.featureStockRulesStockItemRuleDisable,
    SettingsReferenceEnum.featureStockRulesStockTags,
    SettingsReferenceEnum.featureStockRulesStockThreshold,
    SettingsReferenceEnum.featureStockRulesPriceBanding,
    SettingsReferenceEnum.featureStockRulesMultiplePriceChanges,
    SettingsReferenceEnum.featureStockRulesAdvancedFilters,
    SettingsReferenceEnum.featureStockTakeGeneral,
  ]);

  /**
   * Feature settings that do not act on the selected header store, so they stay
   * available to users who do not administer it. Copy Settings reads a chosen
   * source store and writes to a chosen target store, both of which it gates on
   * their own admin access.
   */
  private readonly headerStoreIndependentFeatures: ReadonlySet<NavigationPageType> = new Set([
    NavigationPageEnum.AutoOrdering,
    NavigationPageEnum.CopySettings,
  ]);

  protected readonly showStoreSettingsNoPermission = computed((): boolean => {
    if (this.isAdmin()) {
      return false;
    }
    return this.storeAdminRequiredSettings.has(this.selectedSetting());
  });

  /**
   * Page-level title and subtitle shown above each settings page.
   * Keyed by the currently-selected settings reference; the `{{ store }}`
   * token in the subtitle is replaced with the selected store's name (or
   * "this store" as a fallback).
   */
  private readonly pageHeaderMap: Record<number, { title: string; subtitle: string }> = {
    [SettingsReferenceEnum.accountUserProfile]: {
      title: 'User Profile',
      subtitle: 'Manage your profile details, contact information and account preferences.',
    },
    [SettingsReferenceEnum.applicationUserTableSettings]: {
      title: 'Table Settings',
      subtitle: 'Configure how data tables are displayed across the application.',
    },
    [SettingsReferenceEnum.applicationPreferences]: {
      title: 'Preferences',
      subtitle: 'Set application-wide preferences such as date format and store display order.',
    },
    [SettingsReferenceEnum.applicationEmailSettings]: {
      title: 'Email Notifications',
      subtitle: 'Manage which email notifications you receive and who receives them.',
    },
    [SettingsReferenceEnum.featureAutoOrderOrderSettings]: {
      title: 'Auto Ordering — Order Settings',
      subtitle: 'Configure auto-ordering order options for {{ store }}.',
    },
    [SettingsReferenceEnum.featureAutoOrderOpenOrderSettings]: {
      title: 'Auto Ordering — Open Orders',
      subtitle: 'Manage open-order column visibility and view settings for {{ store }}.',
    },
    [SettingsReferenceEnum.featureAutoOrderGeneralSettings]: {
      title: 'Auto Ordering — General',
      subtitle: 'Configure default auto-ordering behaviour and override settings for {{ store }}.',
    },
    [SettingsReferenceEnum.featureAutoOrderSchedulerSettings]: {
      title: 'Auto Ordering — Scheduler',
      subtitle: 'Configure auto-ordering scheduler options for {{ store }}.',
    },
    [SettingsReferenceEnum.featureAutoOrderDocumentLayouts]: {
      title: 'Purchase Order Layout',
      subtitle: 'Layout 0 is generated on the client’s server and stays the default for {{ store }}. Choose another layout to preview it.',
    },
    [SettingsReferenceEnum.featureShelfTalkerGeneral]: {
      title: 'Shelf Talkers',
      subtitle: 'Configure shelf-talker layouts and printing options for {{ store }}.',
    },
    [SettingsReferenceEnum.featureLabelMaker]: {
      title: 'Label Maker',
      subtitle: 'Design and manage label templates for {{ store }}.',
    },
    [SettingsReferenceEnum.featureStockRulesStockItemRuleAdd]: {
      title: 'Add Stock Rule',
      subtitle: 'Configure rules that add stock items based on conditions for {{ store }}.',
    },
    [SettingsReferenceEnum.featureStockRulesStockItemRuleEnable]: {
      title: 'Enable Stock Rule',
      subtitle: 'Configure rules that enable stock items for {{ store }}.',
    },
    [SettingsReferenceEnum.featureStockRulesStockItemRuleDisable]: {
      title: 'Disable Stock Rule',
      subtitle: 'Configure rules that disable stock items for {{ store }}.',
    },
    [SettingsReferenceEnum.featureStockRulesStockTags]: {
      title: 'Stock Tags',
      subtitle: 'Manage the tags used to classify stock items for {{ store }}.',
    },
    [SettingsReferenceEnum.featureStockRulesStockThreshold]: {
      title: 'Stock Price Thresholds',
      subtitle: 'Configure the maximum allowed price increase and decrease for {{ store }}.',
    },
    [SettingsReferenceEnum.featureStockRulesPriceBanding]: {
      title: 'Price Banding',
      subtitle: 'Set how edited prices snap to a friendlier ending. The first matching rule is used.',
    },
    [SettingsReferenceEnum.featureStockRulesMultiplePriceChanges]: {
      title: 'Multiple Price Change Rules',
      subtitle: 'Derive sell prices 2-10 from retail price 1 when prices are edited for {{ store }}.',
    },
    [SettingsReferenceEnum.featureStockRulesAdvancedFilters]: {
      title: 'Advanced Filters',
      subtitle: 'Manage saved advanced filters for {{ store }}. Copy filters between stores from Copy Settings.',
    },
    [SettingsReferenceEnum.featureLocalStorageManager]: {
      title: 'Local Storage Manager',
      subtitle: 'Inspect and clear cached data stored in your browser.',
    },
    [SettingsReferenceEnum.featureStockTakeGeneral]: {
      title: 'Stock Take — General',
      subtitle: 'Configure default stock take behaviour for {{ store }}.',
    },
    [SettingsReferenceEnum.featureCopySettings]: {
      title: 'Copy Settings',
      subtitle: 'Copy price banding and saved filters from one store to another.',
    },
  };

  protected readonly pageHeader = computed((): { title: string; subtitle: string } | null => {
    const ref: SettingsReferenceEnum = this.selectedSetting();
    const entry = this.pageHeaderMap[ref];
    if (!entry) return null;
    const storeName: string = this.selectedStore()?.name?.trim() || 'this store';
    return {
      title: entry.title,
      subtitle: entry.subtitle.replace('{{ store }}', storeName),
    };
  });

  constructor() {
    const path = this.router.url.split('?')[0];
    if (path === '/settings/mail' || path.endsWith('/settings/mail')) {
      this._store.dispatch(settingsSetSelected.setSelectedReference({
        ref: SettingsReferenceEnum.applicationEmailSettings,
      }));
    }
    this.announceMicrosoftMailPopupResult();
    effect(() => {
      const onLabelMaker = this.isLabelMakerSelected();
      this.clearCollapseTimeout();
      this.isSidebarCollapsed.set(onLabelMaker);
    }, {allowSignalWrites: true});
  }

  ngOnDestroy(): void {
    this.clearCollapseTimeout();
  }

  protected onToggleSidebar(): void {
    this.clearCollapseTimeout();
    this.isSidebarCollapsed.update((collapsed: boolean) => !collapsed);
  }

  protected onSidebarMouseEnter(): void {
    this.clearCollapseTimeout();
  }

  protected onSidebarMouseLeave(): void {
    if (!this.isLabelMakerSelected()) {
      return;
    }
    this.clearCollapseTimeout();
    this.collapseTimeout = setTimeout((): void => {
      this.isSidebarCollapsed.set(true);
    }, 800);
  }

  /**
   * Expands the Feature rail when a collapsed accordion header is clicked (main-nav pattern).
   */
  protected onSidebarExpandRequest(): void {
    this.clearCollapseTimeout();
    this.isSidebarCollapsed.set(false);
  }

  /**
   * Handles section header click
   * Automatically selects the first feature in the section
   */
  async onSectionClick(sectionHeader: string): Promise<void> {
    const canProceed: boolean = await this.unsavedChangesService.checkUnsavedChanges();
    if (!canProceed) {
      return;
    }
    this._store.dispatch(settingsSetSelected.setSelectedSection({ref: sectionHeader}));

    const allSections: ISettingsSection[] = this.sections();
    const section = allSections.find((s: ISettingsSection) => s.header === sectionHeader);

    if (section && section.aufFeatures.length > 0) {
      const firstFeature: TFeatures = section.aufFeatures[0];

      if (firstFeature.standalone === false && firstFeature.subFeatures && firstFeature.subFeatures.length > 0) {
        const firstSubFeature = firstFeature.subFeatures[0] as unknown as IUserAccessFeature;
        if (firstSubFeature.ref) {
          this._store.dispatch(settingsSetSelected.setSelectedReference({ref: firstSubFeature.ref}));
          this._store.dispatch(settingsSetSelected.setSelectedSubSection({ref: firstFeature.displayText || ''}));
        }
      } else if (firstFeature.ref) {
        this._store.dispatch(settingsSetSelected.setSelectedReference({ref: firstFeature.ref}));
        this._store.dispatch(settingsSetSelected.setSelectedSubSection({ref: ''}));
      }
    }
  }

  /**
   * Tells the window that opened Microsoft sign-in when this page was opened as the popup return.
   */
  private announceMicrosoftMailPopupResult(): void {
    const params = this.router.parseUrl(this.router.url).queryParams;
    const status = params['microsoft'];
    if (status !== 'connected' && status !== 'admin-consented' && status !== 'error') {
      return;
    }
    const payload: {type: string; status: string; email?: string; message?: string} = {
      type: GALLIX_MICROSOFT_MAIL_MESSAGE,
      status,
      email: typeof params['email'] === 'string' ? params['email'] : undefined,
      message: typeof params['message'] === 'string' ? params['message'] : undefined,
    };
    const channel = new BroadcastChannel(GALLIX_MICROSOFT_MAIL_MESSAGE);
    channel.postMessage(payload);
    channel.close();
    try {
      if (window.opener && window.opener !== window) {
        window.opener.postMessage(payload, window.location.origin);
      }
    } catch {
      // The opener can be unreachable after Microsoft sign-in.
    }
    if (window.name === GALLIX_MICROSOFT_MAIL_MESSAGE) {
      window.close();
    }
  }

  /**
   * Marks feature section entries as disabled for users who do not administer the
   * selected store. Only features that do not act on that store stay enabled:
   * Auto Ordering with the General Settings subFeature, and Copy Settings.
   */
  private filterNonAdminFeatures(features: TFeatures[]): TFeatures[] {
    return features.map((f: TFeatures) => {
      const isHeaderStoreIndependent: boolean = this.headerStoreIndependentFeatures.has(f.feature);
      return {
        ...f,
        disabled: !isHeaderStoreIndependent,
        subFeatures: f.subFeatures?.map((sub) => ({
          ...sub,
          disabled: (sub as unknown as IUserAccessFeature).feature !== NavigationPageEnum.AutoOrderingGeneralSettings,
        })),
      };
    });
  }

  private clearCollapseTimeout(): void {
    if (this.collapseTimeout !== null) {
      clearTimeout(this.collapseTimeout);
      this.collapseTimeout = null;
    }
  }

}
